import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { usePaywall } from './PaywallProvider.js';
import { startAnnualCheckout } from './billingActions.js';
import {
  getLearnMoreExitPath,
  getCheckoutReturnPath,
  resolveLearnMoreSecondaryHint,
  resolveLearnMoreSecondaryLabel,
} from './learnMoreNavigation.js';
import './paywall.scss';
import MembershipBenefitsList from './MembershipBenefitsList.js';
import { MEMBERSHIP_HEADLINE } from './membershipBenefits.js';

function resolvePrimaryCtaLabel(access, intent) {
  const isRenewIntent = intent === 'renew'
    || access?.tier === 'expired'
    || access?.showRenewInNavbar;

  return isRenewIntent ? 'Renew Now!' : 'Join Now!';
}

export default function LearnMore({ isAuthenticated = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const intent = searchParams.get('intent');
  const { access, refreshAccess } = usePaywall();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState(null);

  const ctaLabel = useMemo(
    () => resolvePrimaryCtaLabel(access, intent),
    [access, intent]
  );

  const secondaryCtaLabel = useMemo(
    () => resolveLearnMoreSecondaryLabel(access, intent, isAuthenticated),
    [access, intent, isAuthenticated]
  );

  const secondaryCtaHint = useMemo(
    () => resolveLearnMoreSecondaryHint(access, intent, isAuthenticated),
    [access, intent, isAuthenticated]
  );

  const checkoutIntent = intent === 'renew' || access?.tier === 'expired' ? 'renew' : 'join';

  const handleDismiss = useCallback(() => {
    navigate(getLearnMoreExitPath(location));
  }, [location, navigate]);

  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus === 'success') {
      refreshAccess();
      setCheckoutSuccess('Welcome to the Circus! Your annual access is now active.');
      return;
    }
    if (checkoutStatus === 'cancel') {
      setCheckoutError('Checkout was cancelled. You can try again whenever you are ready.');
      return;
    }
    if (checkoutStatus === 'error') {
      setCheckoutError('We could not confirm your payment. Please try again or contact support.');
    }
  }, [refreshAccess, searchParams]);

  const handlePrimaryCta = async () => {
    setCheckoutError(null);
    setCheckoutSuccess(null);

    if (!isAuthenticated) {
      navigate('/loginWithGoogle', { state: { from: '/learn-more' } });
      return;
    }

    setCheckoutLoading(true);
    try {
      const returnTo = getCheckoutReturnPath(location);
      const result = await startAnnualCheckout({ intent: checkoutIntent, returnTo });
      if (result.redirecting) {
        return;
      }

      await refreshAccess();

      if (result.alreadyActive) {
        setCheckoutSuccess('You already have full access to Calculus Circus.');
      } else {
        setCheckoutSuccess('Welcome to the Circus! Your annual access is now active.');
      }
    } catch (error) {
      if (error.code === 'EMAIL_VERIFICATION_REQUIRED') {
        setCheckoutError(
          'Please verify your email before purchasing annual access. Check your inbox or visit your profile to resend the verification email.'
        );
      } else if (error.code === 'STUB_DISABLED' || error.code === 'STRIPE_NOT_CONFIGURED') {
        setCheckoutError('Online checkout is not available yet. Please try again later.');
      } else {
        setCheckoutError(error.message || 'Checkout failed. Please try again.');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const showDismissActions = !checkoutSuccess && !checkoutLoading;

  return (
    <div className="paywall-learn-more">
      <div className="container py-5">
        <div className="paywall-learn-more__card mx-auto">
          {showDismissActions && (
            <button
              type="button"
              className="paywall-learn-more__dismiss"
              onClick={handleDismiss}
              aria-label={secondaryCtaLabel}
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}

          <h1 className="paywall-learn-more__title">
            {MEMBERSHIP_HEADLINE}
          </h1>

          <MembershipBenefitsList variant="learn-more" />

          <div className="paywall-learn-more__cta-wrap text-center">
            <Button
              variant="warning"
              size="lg"
              className="paywall-learn-more__cta"
              onClick={handlePrimaryCta}
              disabled={checkoutLoading || Boolean(checkoutSuccess)}
            >
              {checkoutLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing…
                </>
              ) : (
                ctaLabel
              )}
            </Button>
            <p className="paywall-learn-more__price text-muted mt-3 mb-0">
              $30 for one year of full access — no auto-renewal
            </p>
          </div>

          {showDismissActions && (
            <div className="paywall-learn-more__skip-group">
              <p className="paywall-learn-more__guest-or" aria-hidden="true">
                — or —
              </p>
              <button
                type="button"
                className="paywall-learn-more__skip-btn"
                onClick={handleDismiss}
              >
                {secondaryCtaLabel}
              </button>
              <p className="paywall-learn-more__action-hint text-muted">
                {secondaryCtaHint}
              </p>
            </div>
          )}

          {checkoutSuccess && (
            <Alert variant="success" className="paywall-learn-more__checkout-notice mt-4 mb-0">
              {checkoutSuccess}{' '}
              <Button
                variant="link"
                className="p-0 align-baseline"
                onClick={handleDismiss}
              >
                Go to the pathway
              </Button>
            </Alert>
          )}

          {checkoutError && (
            <Alert variant="danger" className="paywall-learn-more__checkout-notice mt-4 mb-0">
              {checkoutError}
              {checkoutError.includes('verify your email') && (
                <>
                  {' '}
                  <Button
                    variant="link"
                    className="p-0 align-baseline"
                    onClick={() => navigate('/viewProfile')}
                  >
                    Go to profile
                  </Button>
                </>
              )}
            </Alert>
          )}

          {!isAuthenticated && (
            <p className="paywall-learn-more__sign-in-hint text-center text-muted mt-4 mb-0">
              Already have an account?{' '}
              <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                onClick={() => navigate('/loginWithGoogle', { state: { from: '/learn-more' } })}
              >
                Sign in with Google
              </button>
              {' '}to start your free trial.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
